/**
 * SupplierDashboard — the /supplier landing page for role="supplier" accounts.
 * Displays: assigned products (via /products?assigned_supplier=me), own
 * submissions (via /suppliers/submissions), and a modal form to POST
 * /suppliers/submit for a chosen product.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Package, FileUp, CheckCircle2, Clock, LogOut, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import NotificationBell from '@/components/NotificationBell';

const SUB_TYPES = ['pcf', 'energy', 'material', 'transport', 'declaration'];

export default function SupplierDashboard() {
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { id, name }
  const [form, setForm] = useState({ submission_type: 'pcf', payload: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get('/products?assigned_supplier=me'),
        api.get('/suppliers/submissions'),
      ]);
      setProducts(Array.isArray(p.data) ? p.data : []);
      setSubmissions(s.data?.submissions || []);
    } catch (err) {
      toast.error('Could not load your dashboard');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openFor = (p) => {
    setSelected({ id: p.id, name: p.name });
    setForm({ submission_type: 'pcf', payload: '{\n  "kgCO2e": 0,\n  "unit": "kg"\n}' });
  };

  const submit = async (e) => {
    e.preventDefault();
    let parsed;
    try { parsed = JSON.parse(form.payload || '{}'); }
    catch { toast.error('Data must be valid JSON'); return; }
    setSubmitting(true);
    try {
      await api.post('/suppliers/submit', {
        product_id: selected.id,
        submission_type: form.submission_type,
        data: parsed,
      });
      toast.success('Submission received — the buyer will be notified');
      setSelected(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const stats = {
    products: products.length,
    total: submissions.length,
    pending: submissions.filter((s) => s.status === 'submitted').length,
    approved: submissions.filter((s) => s.status === 'approved').length,
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card/80 backdrop-blur-xl px-4 lg:px-6">
        <div className="flex items-center gap-3"><Logo size={32} variant="full" tagline /></div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <span className="text-sm font-medium hidden md:inline" data-testid="supplier-header-name">
            {user?.name || user?.email}
          </span>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="supplier-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <main className="p-6 max-w-6xl mx-auto" data-testid="supplier-dashboard">
        <div className="mb-6">
          <h1 className="text-2xl font-heading font-semibold">Supplier workspace</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Submit primary data for the products your buyer has assigned to you.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard icon={<Package className="w-4 h-4" />} label="Assigned" value={stats.products} testid="stat-products" />
          <StatCard icon={<FileUp className="w-4 h-4" />} label="Total submissions" value={stats.total} testid="stat-total" />
          <StatCard icon={<Clock className="w-4 h-4 text-amber-500" />} label="Pending review" value={stats.pending} testid="stat-pending" />
          <StatCard icon={<CheckCircle2 className="w-4 h-4 text-emerald-500" />} label="Approved" value={stats.approved} testid="stat-approved" />
        </div>

        <Card className="mb-6" data-testid="supplier-products-card">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="font-medium">Assigned products</h2>
            <span className="text-xs text-muted-foreground">{products.length} total</span>
          </div>
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : products.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="supplier-products-empty">
              No products assigned to you yet. Your buyer will assign products from their workspace.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {products.map((p) => (
                <li key={p.id} className="px-5 py-3 flex items-center justify-between" data-testid={`supplier-product-${p.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.sector || 'sector n/a'} · id {String(p.id).slice(0, 8)}</div>
                  </div>
                  <Button size="sm" onClick={() => openFor(p)} data-testid={`supplier-submit-btn-${p.id}`}>
                    <FileUp className="w-3.5 h-3.5 mr-1" /> Submit data
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card data-testid="supplier-submissions-card">
          <div className="px-5 py-3 border-b border-border">
            <h2 className="font-medium">My submissions</h2>
          </div>
          {submissions.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Nothing submitted yet.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {submissions.map((s) => (
                <li key={s.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`supplier-submission-${s.id}`}>
                  <div>
                    <div className="font-medium">Product {String(s.product_id).slice(0, 8)}</div>
                    <div className="text-xs text-muted-foreground">{s.submission_type} · v{s.version} · {new Date(s.submitted_at || s.created_at).toLocaleString()}</div>
                  </div>
                  <StatusBadge status={s.status} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="submit-modal">
          <Card className="w-full max-w-md p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold">Submit data for {selected.name}</h3>
              <Button variant="ghost" size="icon" onClick={() => setSelected(null)} data-testid="submit-cancel-icon">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <form onSubmit={submit} className="mt-4 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="sub-type">Type</Label>
                <select id="sub-type" data-testid="submit-type-select"
                        value={form.submission_type}
                        onChange={(e) => setForm({ ...form, submission_type: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  {SUB_TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sub-data">Data (JSON)</Label>
                <textarea id="sub-data" data-testid="submit-data-textarea"
                          value={form.payload}
                          onChange={(e) => setForm({ ...form, payload: e.target.value })}
                          rows={7} required
                          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setSelected(null)}>Cancel</Button>
                <Button type="submit" className="flex-1" disabled={submitting} data-testid="submit-confirm-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Submitting…</> : 'Submit'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-xs text-muted-foreground flex items-center gap-1.5">{icon}{label}</div>
      <div className="text-2xl font-heading font-semibold mt-1">{value}</div>
    </Card>
  );
}

function StatusBadge({ status }) {
  const map = {
    submitted: { cls: 'bg-amber-500/10 text-amber-600 border-amber-500/30', label: 'Submitted' },
    under_review: { cls: 'bg-blue-500/10 text-blue-600 border-blue-500/30', label: 'Under review' },
    approved: { cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', label: 'Approved' },
    rejected: { cls: 'bg-red-500/10 text-red-600 border-red-500/30', label: 'Rejected' },
    draft: { cls: 'bg-muted text-muted-foreground border-border', label: 'Draft' },
  };
  const m = map[status] || map.draft;
  return <Badge variant="outline" className={`${m.cls} text-[10px] uppercase tracking-wide`}>{m.label}</Badge>;
}
